/** @jsxImportSource @emotion/react */
import { useMemo, useState } from "react";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { ObjectUtils } from "primereact/utils";
import BotonExportarExcel from "../botonExportarExcel";
import { columnasExportablesDesde } from "../../utils/columnasExportables";

// La tabla de la plataforma: un <DataTable> de PrimeReact (mismas props y mismos
// <Column> hijos) con botón de Excel y, por defecto, barra de filtros
// combinables (búsqueda global + desplegables por campo) y paginación.
// - Las opciones de cada desplegable salen de los valores presentes en `value`
//   (no de un catálogo aparte), y cada desplegable se oculta si tiene <=1
//   opción (salvo que el filtro tenga `siempreVisible`).
// - Con `conFiltros={false}` no muestra búsqueda ni filtros ni impone
//   paginación ni filas alternadas: es un <DataTable> con botón de Excel.
// - El Excel exporta lo que la tabla muestra, ya filtrado, ordenado y paginado
//   (PrimeReact lo informa por `onValueChange`).
export default function TablaFiltrable({
  value,
  conFiltros = true,
  filtros = [],
  searchFields = [],
  emptyMessage,
  rows,
  // Por defecto la búsqueda va primero (izquierda) con el mismo ancho que los
  // desplegables — así se comportan hoy todos los usos existentes. Algunas
  // pantallas (importaciones) la quieren al final (derecha) y más ancha.
  busquedaAlFinal = false,
  anchoBusqueda,
  // Si se pasa, aparece un botón para descargar en Excel exactamente lo que
  // está filtrado/buscado en ese momento (no vuelve a pedir nada al backend).
  // Las columnas se derivan de las <Column> hijas (las que tienen `header`;
  // las de solo iconos/botones de acción se ignoran).
  nombreArchivo,
  // Sustituye la exportación por defecto cuando las columnas de la tabla no
  // representan todo el contenido (ver BotonExportarExcel).
  exportar,
  // Estado de carga del botón, para cuando `exportar` pide el archivo a la API.
  exportando,
  onValueChange,
  children,
  ...rest
}) {
  const [busqueda, setBusqueda] = useState("");
  const [seleccion, setSeleccion] = useState({});

  const opcionesDesde = (valores) =>
    [...new Set(valores)]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ label: v, value: v }));

  const opcionesPorFiltro = useMemo(
    () =>
      Object.fromEntries(
        filtros.map((f) => [
          f.campo,
          opcionesDesde(
            f.multiple
              ? (value || []).flatMap((row) => row[f.campo] || [])
              : (value || []).map((row) => row[f.campo]),
          ),
        ]),
      ),
    [filtros, value],
  );

  const datosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return (value || []).filter((row) => {
      if (texto) {
        const enFila = searchFields
          .map((campo) => {
            // Igual que globalFilterFields de PrimeReact: acepta paths
            // anidados ("proceso.nombre").
            const v = ObjectUtils.resolveFieldData(row, campo);
            return Array.isArray(v) ? v.join(" ") : v;
          })
          .join(" ")
          .toLowerCase();
        if (!enFila.includes(texto)) return false;
      }
      for (const f of filtros) {
        const seleccionados = seleccion[f.campo] || [];
        if (!seleccionados.length) continue;
        if (f.multiple) {
          const valores = row[f.campo] || [];
          if (!seleccionados.some((s) => valores.includes(s))) return false;
        } else if (!seleccionados.includes(row[f.campo])) {
          return false;
        }
      }
      return true;
    });
  }, [value, busqueda, seleccion, filtros, searchFields]);

  const anchoControl = {
    width: "13em",
    "@media (max-width: 768px)": { width: "100%" },
  };

  const columnasExportables = columnasExportablesDesde(children);

  // Lo que la tabla muestra tras paginar/ordenar, junto con los datos de los
  // que salió: PrimeReact no llama a `onValueChange` cuando cambian los datos,
  // así que si llegaron otros lo procesado quedó viejo y se exportan los datos.
  const [procesado, setProcesado] = useState(null);
  const visibles =
    procesado?.origen === datosFiltrados ? procesado.datos : datosFiltrados;

  const busquedaEl = (
    <span
      key="busqueda"
      className="p-input-icon-left"
      css={{
        "& .pi-search": { left: "0.75rem" },
        ...(anchoBusqueda
          ? {
              width: anchoBusqueda,
              "@media (max-width: 768px)": { width: "100%" },
            }
          : anchoControl),
      }}
    >
      <i className="pi pi-search" />
      <InputText
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar..."
        css={{ width: "100%", paddingLeft: "2.5rem" }}
      />
    </span>
  );

  // `siempreVisible` en un filtro lo muestra aunque tenga una sola opción
  // (p. ej. todos los certificados del cliente en el mismo estado)
  const filtrosEl = filtros.map(
    (f) =>
      opcionesPorFiltro[f.campo].length > (f.siempreVisible ? 0 : 1) && (
        <MultiSelect
          key={f.campo}
          value={seleccion[f.campo] || []}
          options={opcionesPorFiltro[f.campo]}
          onChange={(e) =>
            setSeleccion((prev) => ({ ...prev, [f.campo]: e.value }))
          }
          placeholder={f.label}
          maxSelectedLabels={1}
          selectedItemsLabel={`{0} ${f.labelPlural || f.label.toLowerCase()}`}
          filter
          showClear
          css={anchoControl}
        />
      ),
  );

  const conExcel = Boolean(nombreArchivo || exportar);

  return (
    <>
      {(conFiltros || conExcel) && (
        <div
          css={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5em",
            alignItems: "center",
            justifyContent: "flex-end",
            marginBottom: "0.5em",
          }}
        >
          {conFiltros &&
            (busquedaAlFinal ? (
              <>
                {filtrosEl}
                {busquedaEl}
              </>
            ) : (
              <>
                {busquedaEl}
                {filtrosEl}
              </>
            ))}
          {conExcel && (
            <BotonExportarExcel
              data={visibles}
              columnas={columnasExportables}
              nombreArchivo={nombreArchivo}
              exportar={exportar}
              exportando={exportando}
            />
          )}
        </div>
      )}
      <DataTable
        value={datosFiltrados}
        {...(conFiltros
          ? { paginator: true, rows: rows ?? 10, stripedRows: true }
          : rows
            ? { rows }
            : {})}
        emptyMessage={emptyMessage}
        onValueChange={(v) => {
          setProcesado({ origen: datosFiltrados, datos: v });
          onValueChange?.(v);
        }}
        {...rest}
      >
        {children}
      </DataTable>
    </>
  );
}
